import React, { useState } from 'react';
import './PerformanceChart.css';

const W = 600;
const H = 180;
const PAD_X = 16;
const PAD_Y = 20;

export default function PerformanceChart({ data }) {
    const [tooltip, setTooltip] = useState(null);

    if (!data || data.length === 0) {
        return (
            <div className="perf-empty">
                <span>Sin partidos registrados todavía</span>
            </div>
        );
    }

    const cumulative = data.reduce((acc, point, i) => {
        const prev = i === 0 ? 0 : acc[i - 1].score;
        acc.push({ ...point, score: prev + (point.won ? 1 : -1) });
        return acc;
    }, []);

    const scores = cumulative.map(p => p.score);
    const minScore = Math.min(0, ...scores);
    const maxScore = Math.max(0, ...scores);
    const range = maxScore - minScore || 1;

    const toX = (i) => PAD_X + (i / (cumulative.length - 1 || 1)) * (W - PAD_X * 2);
    const toY = (score) => PAD_Y + (1 - (score - minScore) / range) * (H - PAD_Y * 2);

    const zeroY = toY(0);

    const linePoints = cumulative.map((p, i) => `${toX(i)},${toY(p.score)}`).join(' ');

    const firstX  = toX(0);
    const lastX   = toX(cumulative.length - 1);
    const bottomY = H - PAD_Y;
    const areaPoints = `${firstX},${bottomY} ${linePoints} ${lastX},${bottomY}`;

    const gradientId = 'perf-gradient';

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    };

    return (
        <div className="perf-chart-wrapper">
            <svg
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                className="perf-svg"
            >
                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" style={{ stopColor: 'var(--brand-accent)', stopOpacity: 0.45 }} />
                        <stop offset="100%" style={{ stopColor: 'var(--brand-accent)', stopOpacity: 0.03 }} />
                    </linearGradient>
                </defs>

                <line
                    x1={PAD_X} y1={zeroY}
                    x2={W - PAD_X} y2={zeroY}
                    stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 4"
                />

                <polygon
                    points={areaPoints}
                    fill={`url(#${gradientId})`}
                />

                <polyline
                    points={linePoints}
                    fill="none"
                    style={{ stroke: 'var(--brand-accent)' }}
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                />

                {cumulative.map((p, i) => {
                    const cx = toX(i);
                    const cy = toY(p.score);
                    return (
                        <circle
                            key={i}
                            cx={cx} cy={cy} r="5"
                            style={{ fill: p.won ? 'var(--brand-accent)' : '#f43f5e' }}
                            stroke="white" strokeWidth="2"
                            className="perf-dot"
                            onMouseEnter={() => setTooltip({ i, cx, cy, p })}
                            onMouseLeave={() => setTooltip(null)}
                        />
                    );
                })}

                {tooltip && (() => {
                    const { cx, cy, p } = tooltip;
                    const label = `${formatDate(p.date)} · ${p.won ? 'Victoria' : 'Derrota'} (${p.score > 0 ? '+' : ''}${p.score})`;
                    const textW = label.length * 6.5 + 16;
                    const tx = Math.min(Math.max(cx - textW / 2, PAD_X), W - PAD_X - textW);
                    const ty = cy - 36;
                    return (
                        <g>
                            <rect
                                x={tx} y={ty}
                                width={textW} height={22}
                                rx="6" ry="6"
                                fill="#1e293b" opacity="0.92"
                            />
                            <text
                                x={tx + textW / 2} y={ty + 15}
                                textAnchor="middle"
                                fill="white"
                                fontSize="11"
                                fontFamily="inherit"
                            >
                                {label}
                            </text>
                        </g>
                    );
                })()}
            </svg>

            <div className="perf-legend">
                <span className="perf-legend-item perf-legend-win">
                    <span className="perf-dot-small"></span> Victoria
                </span>
                <span className="perf-legend-item perf-legend-loss">
                    <span className="perf-dot-small"></span> Derrota
                </span>
                <span className="perf-legend-score">
                    Balance: {scores[scores.length - 1] >= 0 ? '+' : ''}{scores[scores.length - 1]}
                </span>
            </div>
        </div>
    );
}
